import { useState } from 'react';
import { t } from '@/i18n';
import type { LabeledListField } from '@/types/labeled-list';
import { AccordionField } from '@/components/common/AccordionField';
import { ValueFormattingSection } from '@/components/common/ValueFormattingSection';
import { DeltaFormattingSection } from '@/components/common/DeltaFormattingSection';
import { DeltaColorsSection } from '@/components/common/DeltaColorsSection';
import { IconPicker } from '@/components/common/IconPicker';
import { ColorPicker } from '@/components/common/ColorPicker';
import './LabeledListSettings.css';

interface LabeledListSettingsProps {
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
}

export function LabeledListSettings({ config, onConfigChange }: LabeledListSettingsProps) {
  const fields = ((config.fields as LabeledListField[]) ?? []);
  const [expandedField, setExpandedField] = useState<string | null>(null);
  const [newFieldLabel, setNewFieldLabel] = useState('');

  const handlePanelDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onConfigChange({ ...config, description: e.target.value });
  };

  const updateFields = (newFields: LabeledListField[]) => {
    onConfigChange({ ...config, fields: newFields });
  };

  const handleAddField = () => {
    const label = newFieldLabel.trim();
    if (!label) return;
    const fieldName = `field_${Date.now()}`;
    updateFields([...fields, { fieldName, label }]);
    setNewFieldLabel('');
  };

  const handleRemoveField = (fieldName: string) => {
    updateFields(fields.filter((f) => f.fieldName !== fieldName));
    if (expandedField === fieldName) {
      setExpandedField(null);
    }
  };

  const handleMoveUp = (fieldName: string) => {
    const idx = fields.findIndex((f) => f.fieldName === fieldName);
    if (idx <= 0) return;
    const newFields = [...fields];
    [newFields[idx - 1], newFields[idx]] = [newFields[idx], newFields[idx - 1]];
    updateFields(newFields);
  };

  const handleMoveDown = (fieldName: string) => {
    const idx = fields.findIndex((f) => f.fieldName === fieldName);
    if (idx >= fields.length - 1) return;
    const newFields = [...fields];
    [newFields[idx], newFields[idx + 1]] = [newFields[idx + 1], newFields[idx]];
    updateFields(newFields);
  };

  const handleFieldSettingChange = (fieldName: string, key: string, value: unknown) => {
    updateFields(
      fields.map((f) =>
        f.fieldName === fieldName ? { ...f, [key]: value } : f,
      ),
    );
  };

  const handleToggle = (fieldName: string) => {
    setExpandedField((prev) => prev === fieldName ? null : fieldName);
  };

  return (
    <div>
      <div className="mb-3">
        <label className="form-label">{t('dashboard.widget.labeled_list.field.panelDescription')}</label>
        <textarea
          className="form-control"
          rows={3}
          data-testid="labeled-list-panel-description"
          value={(config.description as string) ?? ''}
          onChange={handlePanelDescriptionChange}
        />
      </div>

      <div className="mb-3">
        <label className="form-label">{t('dashboard.widget.labeled_list.displayMode')}</label>
        <div className="btn-group" role="group">
          <button
            type="button"
            className={`btn btn-sm ${(config.displayMode ?? 'list') === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => onConfigChange({ ...config, displayMode: 'list' })}
            data-testid="labeled-list-display-mode-list"
          >
            <i className="las la-list me-1" /> {t('dashboard.widget.labeled_list.displayModeList')}
          </button>
          <button
            type="button"
            className={`btn btn-sm ${config.displayMode === 'badges' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => onConfigChange({ ...config, displayMode: 'badges' })}
            data-testid="labeled-list-display-mode-badges"
          >
            <i className="las la-grip-horizontal me-1" /> {t('dashboard.widget.labeled_list.displayModeBadges')}
          </button>
        </div>
      </div>

      <div className="accordion">
        {fields.map((field, idx) => {
          const testIdPrefix = `labeled-list-field-${field.fieldName}`;
          const isExpanded = expandedField === field.fieldName;

          return (
            <AccordionField
              key={field.fieldName}
              fieldName={field.fieldName}
              label={field.label || field.fieldName}
              isExpanded={isExpanded}
              onToggle={handleToggle}
              testIdPrefix={testIdPrefix}
            >
              <div className="mb-3">
                <label className="form-label">{t('dashboard.widget.indicator.field.label')}</label>
                <input
                  type="text"
                  className="form-control"
                  data-testid={`${testIdPrefix}-label`}
                  value={field.label}
                  onChange={(e) => handleFieldSettingChange(field.fieldName, 'label', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">{t('dashboard.widget.labeled_list.field.icon')}</label>
                <IconPicker
                  value={(field.icon as string) ?? ''}
                  onChange={(iconClass) => handleFieldSettingChange(field.fieldName, 'icon', iconClass)}
                />
              </div>

              {config.displayMode === 'badges' && (
                <>
                  <div className="mb-3" data-testid={`labeled-list-field-${field.fieldName}-badge-color`}>
                    <label className="form-label">{t('dashboard.widget.labeled_list.field.badgeColor')}</label>
                    <ColorPicker
                      value={(field.badgeColor as string) ?? '#dee2e6'}
                      onChange={(color) => handleFieldSettingChange(field.fieldName, 'badgeColor', color)}
                    />
                  </div>
                  <div className="mb-3" data-testid={`labeled-list-field-${field.fieldName}-chevron-icon`}>
                    <label className="form-label">{t('dashboard.widget.labeled_list.field.chevronIcon')}</label>
                    <div className="form-text">
                      {t('dashboard.widget.labeled_list.field.chevronIconHint')}
                    </div>
                    <div className="chevron-picker">
                      {[
                        { icon: 'la-angle-right', title: '›' },
                        { icon: 'la-angle-double-right', title: '»' },
                        { icon: 'la-arrow-right', title: '→' },
                        { icon: 'la-plus', title: '+' },
                        { icon: 'la-minus', title: '−' },
                        { icon: 'la-equals', title: '=' },
                        { icon: 'none', title: t('dashboard.widget.labeled_list.field.chevronNone') },
                      ].map(({ icon, title }) => {
                        const current = field.chevronIcon ?? 'la-angle-right';
                        const isSelected = icon === current;
                        return (
                          <button
                            key={icon}
                            type="button"
                            title={title}
                            className={`chevron-picker__btn${isSelected ? ' chevron-picker__btn--selected' : ''}`}
                            onClick={() => handleFieldSettingChange(field.fieldName, 'chevronIcon', icon)}
                          >
                            {icon !== 'none' ? <i className={`las ${icon}`} /> : <span className="chevron-picker__none">∅</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              <div className="mb-3">
                <label className="form-label">{t('dashboard.widget.indicator.field.description')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  data-testid={`${testIdPrefix}-description`}
                  value={field.description ?? ''}
                  onChange={(e) => handleFieldSettingChange(field.fieldName, 'description', e.target.value)}
                />
              </div>

              <ValueFormattingSection
                prefix={field.prefix}
                suffix={field.suffix}
                useMillions={field.useMillions}
                millionsFrom={field.millionsFrom}
                decimalPlaces={field.decimalPlaces}
                onChange={(key, value) => handleFieldSettingChange(field.fieldName, key, value)}
                testIdPrefix={testIdPrefix}
              />

              <DeltaFormattingSection
                deltaPrefix={field.deltaPrefix}
                deltaSuffix={field.deltaSuffix}
                useMillionsDelta={field.useMillionsDelta}
                millionsFromDelta={field.millionsFromDelta}
                decimalPlacesDelta={field.decimalPlacesDelta}
                onChange={(key, value) => handleFieldSettingChange(field.fieldName, key, value)}
                testIdPrefix={testIdPrefix}
              />

              <DeltaColorsSection
                deltaColorUp={field.deltaColorUp}
                deltaColorDown={field.deltaColorDown}
                deltaColorNeutral={field.deltaColorNeutral}
                onChange={(key, value) => handleFieldSettingChange(field.fieldName, key, value)}
                testIdPrefix={testIdPrefix}
              />

              <div className="d-flex gap-1 mt-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  data-testid={`${testIdPrefix}-move-up`}
                  disabled={idx === 0}
                  onClick={() => handleMoveUp(field.fieldName)}
                >
                  <i className="las la-arrow-up" /> {t('dashboard.widget.labeled_list.moveUp')}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  data-testid={`${testIdPrefix}-move-down`}
                  disabled={idx === fields.length - 1}
                  onClick={() => handleMoveDown(field.fieldName)}
                >
                  <i className="las la-arrow-down" /> {t('dashboard.widget.labeled_list.moveDown')}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm ms-auto"
                  data-testid={`labeled-list-remove-field-${field.fieldName}`}
                  onClick={() => handleRemoveField(field.fieldName)}
                >
                  <i className="las la-trash" /> {t('dashboard.delete')}
                </button>
              </div>
            </AccordionField>
          );
        })}
      </div>

      <div className="input-group mt-2">
        <input
          type="text"
          className="form-control"
          placeholder={t('dashboard.widget.indicator.field.label')}
          data-testid="labeled-list-new-field-label"
          value={newFieldLabel}
          onChange={(e) => setNewFieldLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddField(); }}
        />
        <button
          type="button"
          className="btn btn-outline-primary"
          data-testid="labeled-list-add-field"
          onClick={handleAddField}
        >
          <i className="las la-plus" /> {t('dashboard.addField')}
        </button>
      </div>
    </div>
  );
}
